// Checkbox: a native input drawn square, taken from the invention
// harvesting table. The look lives in Checkbox.css as the `.checkbox`
// class, so this wrapper and plain-HTML consumers render the same
// thing. Guidelines: Storybook, Components/Checkbox.

import { forwardRef, useEffect, useImperativeHandle, useRef, type InputHTMLAttributes, type ReactNode } from 'react';
import './Checkbox.css';

export interface CheckboxProps extends Omit<InputHTMLAttributes<HTMLInputElement>, 'type'> {
  /** Visible label. Without one, give the checkbox an `aria-label`. */
  label?: ReactNode;
  /** Some but not all of what it controls are selected (a select-all box). */
  indeterminate?: boolean;
}

export const Checkbox = forwardRef<HTMLInputElement, CheckboxProps>(
  ({ label, indeterminate = false, className, ...props }, ref) => {
    const inner = useRef<HTMLInputElement>(null);
    useImperativeHandle(ref, () => inner.current as HTMLInputElement);
    useEffect(() => {
      if (inner.current) inner.current.indeterminate = indeterminate;
    }, [indeterminate]);

    const input = <input ref={inner} type="checkbox" className={['checkbox', className].filter(Boolean).join(' ')} {...props} />;
    if (!label) return input;
    return (
      <label className="checkbox-field">
        {input}
        <span>{label}</span>
      </label>
    );
  }
);

Checkbox.displayName = 'Checkbox';
